/**
 * # AsErrorInfo Component
 * 
 * A component for displaying error information to users in a structured format.
 * This component displays error details, timestamps, and provides copy and undo functionality.
 * 
 * ## Features
 * - Displays detailed error information in a clean, structured format
 * - Copy to clipboard functionality for error details
 * - Undo functionality to revert to previous states
 * - Styled error panel with customizable accent color
 * 
 * ## Component Hierarchy
 * ```
 * AsErrorInfo (as-error-info)
 * ├── as-panel-header - Header section using accent color
 * ├── .error-description - Descriptive text about the error
 * ├── .error-info - Container for error details
 * │   ├── .error-info-content - Rows of error information
 * │   └── as-text-button.error-copy-button - Button to copy error details
 * └── as-text-button.error-undo-button - Button to undo to previous state
 * ```
 * 
 * ## CSS Custom Properties
 * - `--error-header-accent-col`: Accent color for the error panel header (default: #ca2222)
 * 
 * ## Events
 * ### Listened Events
 * - `system-info`: Receives system information to display
 * - `new-error`: Receives new error information to display
 * 
 * ## Methods
 * - `updateErrorInfo()`: Updates the displayed error information
 */

import '../panels/as-panel.ts'
import '../panels/subcomps/as-panel-header.ts'
import '../base/buttons/as-text-button.ts'
import { AsTextButton } from '../base/buttons/as-text-button.ts';
import { AsTooltip } from '../overlays/as-tooltip.ts';
import { devlog } from '../../logic/utils.ts';
import styleText from './as-error-info.css?raw';

const errorRow = (rowKey: string, rowValue: string) => /*html*/`
<div class="error-row">
  <as-text class="error-label-text" dict-key="errorPanel.label.${rowKey}">
    ${rowKey}
  </as-text>
  :
  <expand-flex></expand-flex>
  <span class="value-text">
    ${rowValue}
  </span>
</div>
`;

class AsErrorInfo extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private tooltipInfo: AsTooltip | null = null;
  private tooltipResponse: AsTooltip | null = null;
  private copyButton: AsTextButton | null = null;
  private undoButton: AsTextButton | null = null;

  private errorInfo: {
    timestamp: string;
    sessionId: string;
    requestId: string | undefined;
    error: Error;
    info: string | undefined;
    serverCode: string;
  } = {
    timestamp: new Date().toUTCString(),
    sessionId: '...',
    requestId: undefined,
    error: new Error('empty error'),
    info: undefined,
    serverCode: '-1'
  };

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsErrorInfo.sheet
    ];
    this.init(); // Initial rendering
  }

  connectedCallback() {
    this.setupEventListeners();
    // devlog("AS-ERROR-INFO: Getting system info");
    window.dispatchEvent(new CustomEvent('get-system-info', { detail: { requester: this } }));
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  init = () => {
    this.render();

  }

  rerender = () => {
    this.render();
    this.setupEventListeners(); // Reattach listeners after rendering
  }

  render = () => {
    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = /*html*/`
        <as-panel-header> 
          <as-text dict-key="Base.ErrorHeader">
            Quite complex stuff...
          </as-text>
        </as-panel-header>

        <as-text class="error-description" dict-key="Base.ErrorInfo.ErrorDescription.Generic">
          We are very sorry! But something went wrong.\n
          You can copy the following information and send it to the support team.\n
          Press Undo to get back to a previous working state.
        </as-text>
        <div class="error-info">
          <div class="error-info-content">
            ${Object.entries(this.errorInfo).map(([key, value]) =>
              (value) && errorRow(key, value.toString())
            ).join('')}
          </div>
          
          <as-text-button class="error-copy-button">
            <as-text dict-key="Base.ErrorPanel.Button.CopyToClipboard">
              Copy
            </as-text>
          </as-text-button>

        </div> <!-- error-info -->

        <as-text-button class="error-undo-button">
          <as-text dict-key="Base.ErrorPanel.Button.Undo">
            Undo
          </as-text>
        </as-text-button>
      `;
      this.tooltipInfo = this.shadowRoot!.querySelector('as-tooltip.info') as AsTooltip;
      this.tooltipResponse = this.shadowRoot!.querySelector('as-tooltip.response') as AsTooltip;
      this.copyButton = this.shadowRoot!.querySelector('.error-copy-button') as AsTextButton;
      this.undoButton = this.shadowRoot!.querySelector('.error-undo-button') as AsTextButton;
    }
  }

  setupEventListeners = () => {
    const copyButton = this.shadowRoot!.querySelector('.error-copy-button') as AsTextButton;
    const undoButton = this.shadowRoot!.querySelector('.error-undo-button') as AsTextButton;

    this.removeEventListeners(); // Remove any existing listeners

    if (copyButton) {
      copyButton.addEventListener('click', this.handleCopyButtonClick);
    }
    if (undoButton) {
      undoButton.addEventListener('click', this.handleUndoButtonClick);
    }

    this.addEventListener('system-info', this.handleSystemInfo);
    this.addEventListener('new-error', this.handleNewError);
  }

  removeEventListeners = () => {
    const copyButton = this.shadowRoot!.querySelector('.error-copy-button') as AsTextButton;
    const undoButton = this.shadowRoot!.querySelector('.error-undo-button') as AsTextButton;

    if (copyButton) {
      copyButton.removeEventListener('click', this.handleCopyButtonClick);
    }
    if (undoButton) {
      undoButton.removeEventListener('click', this.handleUndoButtonClick);
    }

    this.removeEventListener('system-info', this.handleSystemInfo);
    this.removeEventListener('new-error', this.handleNewError);
  }

  handleCopyButtonClick = (event: Event) => {
    devlog("Copy button clicked", event);
    const errorJson = JSON.stringify(this.errorInfo, null, 2);
    navigator.clipboard.writeText(errorJson);
    devlog("Flashing tooltip", this.tooltipResponse);
    this.copyButton?.showResponse({textKey: 'Base.Actions.Copy.Success', defaultText: 'Copied!'});
  }

  handleUndoButtonClick = () => {
    devlog("Undo button clicked");
    // Add your undo logic here
  }

  handleSystemInfo = (event: CustomEvent) => {
    // devlog("Got system info", event.detail);
    for (const [key, value] of Object.entries(event.detail.info)) {
      this.errorInfo[key] = value;
    }
    this.rerender(); // Use rerender to update the component
  }

  handleNewError = (event: CustomEvent) => {
    devlog("New error", event.detail);
    this.updateErrorInfo(event.detail);
    this.rerender(); // Use rerender to update the component
  }

  updateErrorInfo = (newErrorInfo: Partial<typeof this.errorInfo>) => {
    this.errorInfo = {
      ...this.errorInfo,
      ...newErrorInfo,
      timestamp: new Date().toUTCString()
    };
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-error-info')) {
  customElements.define('as-error-info', AsErrorInfo);
} else {
  console.warn("as-error-info already defined");
}
